import {useCallback, useEffect, useMemo, useState} from 'react';

import {GenericTreeNode, getAllLeaveKeys, groupSelectedKeys, ungroupSelectedKeys} from 'utils/treeUtils';

import {TreeBasicNode} from 'components/Tree/TreeTypes';

export function useOptimizedSelection({
  treeData,
  setSelectedNone,
  selected,
  setSelected: setSelectedPassed,
  groupSelection,
  isAllSelected: isAllSelectedPassed,
  disabled,
  onAllSelected,
  showAllSelectionCount = false,
}: {
  treeData: GenericTreeNode<TreeBasicNode>[];
  setSelectedNone: () => void;
  selected: string[];
  setSelected: (newSelected: string[]) => void;
  groupSelection?: boolean;
  isAllSelected?: boolean;
  disabled?: boolean;
  onAllSelected?: () => void;
  showAllSelectionCount?: boolean;
}) {
  const allLeaveKeys = useMemo(() => {
    return getAllLeaveKeys(treeData);
  }, [treeData]);

  const [isAllSelectedInternal, setIsAllSelectedInternal] = useState(isAllSelectedPassed || false);

  const isAllSelected = isAllSelectedPassed === undefined ? isAllSelectedInternal : isAllSelectedPassed;

  const setSelectedAll = useCallback(() => {
    setSelectedNone();
    setIsAllSelectedInternal(true);
    onAllSelected?.();
  }, [onAllSelected, setSelectedNone]);

  useEffect(() => {
    setIsAllSelectedInternal(false);
  }, [allLeaveKeys]);

  useEffect(() => {
    if (showAllSelectionCount) {
      return;
    }

    if (selected.length > 1 && allLeaveKeys.length === selected.length) {
      setSelectedAll();
    }
  }, [
    allLeaveKeys.length,
    isAllSelectedInternal,
    isAllSelectedPassed,
    selected.length,
    setSelectedAll,
    showAllSelectionCount,
  ]);

  useEffect(() => {
    if (disabled) {
      if (selected.length) {
        setSelectedNone();
      }

      if (isAllSelectedInternal) {
        setIsAllSelectedInternal(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we don't need to listen for setSelectedNone here
  }, [disabled, isAllSelectedInternal, selected]);

  const setSelected = useCallback(
    (newSelected: string[]) => {
      if (disabled) {
        return;
      }

      const ungroupedSelected = ungroupSelectedKeys(treeData, newSelected);

      if (ungroupedSelected.size === allLeaveKeys.length && !showAllSelectionCount) {
        setSelectedNone();
        setIsAllSelectedInternal(true);
        onAllSelected?.();
        return;
      }

      setIsAllSelectedInternal(false);

      if (groupSelection && !showAllSelectionCount) {
        setSelectedPassed(Array.from(groupSelectedKeys(treeData, newSelected)));
      } else {
        setSelectedPassed(newSelected);
      }
    },
    [
      allLeaveKeys.length,
      disabled,
      groupSelection,
      onAllSelected,
      setSelectedNone,
      setSelectedPassed,
      showAllSelectionCount,
      treeData,
    ]
  );

  const visibleSelected = isAllSelected ? allLeaveKeys : selected;

  return {
    setSelected,
    setSelectedNone,
    setSelectedAll,
    selected: visibleSelected,
    selectionCount: showAllSelectionCount ? visibleSelected.length : selected.length === 0 ? 'All' : selected.length,
    disabledKeys: disabled ? allLeaveKeys : [],
  };
}
